// @flow strict

import type {DispositionNode as DispositionNodeType} from 'src/types/chatbot';
import type {NodeEditorProps} from './node-editor-v2.jsx';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {useSelector} from 'react-redux';

// $FlowFixMe[nonstrict-import]
import {useWritebackMetadataV2} from 'src/hooks/useWritebackAllowedAttributes';
// $FlowFixMe[nonstrict-import]
import {useReleaseFlag} from 'src/hooks/product-flags';

import {createRawDraftContentState} from 'src/utils/draft';

import {useOWBFields} from '../hooks';

import {useChatbotStateContext} from '../chatbot-state-context';

// $FlowFixMe[nonstrict-import]
import {
  createDispositionNode,
  createEditableGraph,
  findAncestorNodeVariables,
  getHasAncestorJobMatchQuestionNode,
} from '../utils';

// $FlowFixMe[nonstrict-import]
import LoadedVariablePicker from '../node-editor/variable-picker.jsx';

// $FlowFixMe[nonstrict-import]
import {validateNode} from '../node-editor/validation';

import {Button} from 'src/designSystem2021Components/buttons.jsx';

// $FlowFixMe[nonstrict-import]
import {MessageDraftEditor} from 'src/components/conversation-builder/draft-editor.jsx';

import {Input} from 'src/designSystem2021Components/input.jsx';
import {Select} from 'src/designSystem2021Components/select.jsx';
// $FlowFixMe[nonstrict-import]
import PhoneInput from 'src/components/lib/phone-input';
import {Toggle} from '@spaced-out/ui-design-system/lib/components/Toggle';
import {
  // $FlowFixMe[nonstrict-import]
  useEditorState,
  // $FlowFixMe[nonstrict-import]
  useChatbotURLParams,
} from '../node-editor/reducer';

import css from './node-editor-v2.css';


export default function DispositionNode({
  graph,
  onCancel,
  readOnly,
  isSavingNode,
  setIsSavingNode,
}: NodeEditorProps): React.Node {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const {state, dispatch} = useChatbotStateContext();
  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  const [selectOwnerVar, setSelectOwnerVar] = React.useState(true);

  const editingNodeData = graph.nodes.find(({id}) => id === state.editingNode);
  const defaultNode =
    state.editingNode !== null && editingNodeData?.type === 'disposition'
      ? editingNodeData
      : createDispositionNode();

  const edgeId = Number(state.insertingNode?.edgeId || state.editingNode);

  const [editorState, editorDispatch] = useEditorState<DispositionNodeType>({
    defaultNode,
  });

  const {node} = editorState;
  const {url_params_map: urlParamsMap} = graph.sourcing_config || {};
  const urlParams = useChatbotURLParams(urlParamsMap);

  const setNode = (payload: {...DispositionNodeType}) => {
    editorDispatch({type: 'update', payload});
  };

  const editableGraph = createEditableGraph(graph);
  const showFileUploadNodeVar = Boolean(
    useReleaseFlag('chatbotFileUploadLinkNotificationEnabled'),
  );
  const allowMeetingNodeVariables = Boolean(
    useReleaseFlag('chatbotMeetingNodeVariable'),
  );

  const nodeVariables = [
    ...findAncestorNodeVariables(
      editableGraph,
      defaultNode,
      edgeId,
      showFileUploadNodeVar,
      false,
      allowMeetingNodeVariables,
    ),
  ];

  const writebackAttributes = useWritebackMetadataV2(graph.anchor_entity_type);

  const {getWbFieldId} = useOWBFields();

  const {writeback_field_id} = node;
  const attributeWritebackInfo =
    writeback_field_id && writebackAttributes
      ? [...writebackAttributes.values()].find(
          (metadata) => getWbFieldId(metadata) === writeback_field_id,
        )
      : null;
  const entityDetails = useSelector((state) => state.atsEntities.entities);

  const selectedRelatedEntity = entityDetails.find(
    // $FlowFixMe
    ({name}) => name === attributeWritebackInfo?.related_entity?.entityType,
  );

  const hasAncestorJobMatchQuestionNode = getHasAncestorJobMatchQuestionNode(
    editableGraph,
    defaultNode,
    state.insertingNode?.edgeId,
  );

  const dataType = attributeWritebackInfo?.data_type || 'string';

  const validation = validateNode(node, editableGraph);
  const error = hasSubmitted ? validation : null;

  const handleSaveNode = () => {
    if (validation) {
      setHasSubmitted(true);
    } else {
      state.insertingNode
        ? dispatch({
            type: 'COMMIT_NODE',
            payload: node,
          })
        : dispatch({
            type: 'UPDATE_NODE',
            payload: node,
          });
    }
  };

  React.useEffect(() => {
    if (isSavingNode) {
      handleSaveNode();
      setIsSavingNode(false);
    }
  }, [isSavingNode]);

  let valueInput;
  const possibleValues =
    attributeWritebackInfo?.possible_answers ||
    attributeWritebackInfo?.possible_values?.map((val) => ({
      value: val,
      label: val,
    }));

  if (possibleValues) {
    valueInput = (
      <Select
        searchable
        options={possibleValues}
        value={possibleValues.find(
          ({value}) => value === node.disposition_writeback_value,
        )}
        onChange={(selected) => {
          if (selected?.value) {
            setNode({
              ...node,
              disposition_writeback_value: selected.value,
            });
          }
        }}
        disabled={readOnly}
      />
    );
  } else if (graph.use_case === 'application_inbound_web') {
    // application bots always pass the disposition values back to CWS, so
    // we can uniformly use draft for all application bot disposition nodes
    valueInput = (
      <MessageDraftEditor
        label=""
        placeholder={`${t(
          'Enter_a_value_for_the_ATS_field',
          'Enter a value for the ATS field',
        )}...`}
        defaultValue={node.text_draft_json || createRawDraftContentState()}
        onChange={(text_draft_json) => setNode({...node, text_draft_json})}
        entityType={graph.anchor_entity_type}
        isFirstSmsMessage={false}
        allowGifs={false}
        allowEmojis={false}
        error={error?.text_draft_json}
        readOnly={readOnly}
        useCase={graph.use_case}
        nodeVariables={nodeVariables}
        showDiscoverJobId={hasAncestorJobMatchQuestionNode}
        urlParams={urlParams}
        allowURLParams={true}
        showExternalVariables={Boolean(graph.flow_config.api_bot_enabled)}
      />
    );
  } else {
    switch (dataType) {
      case 'date':
      case 'email':
      case 'string':
        valueInput = (
          <MessageDraftEditor
            label=""
            placeholder={`${t(
              'Enter_a_value_for_the_ATS_field',
              'Enter a value for the ATS field',
            )}...`}
            defaultValue={node.text_draft_json || createRawDraftContentState()}
            onChange={(text_draft_json) => setNode({...node, text_draft_json})}
            entityType={graph.anchor_entity_type}
            isFirstSmsMessage={false}
            allowGifs={false}
            allowEmojis={false}
            error={error?.text_draft_json}
            readOnly={readOnly}
            useCase={graph.use_case}
            nodeVariables={nodeVariables}
            showDiscoverJobId={hasAncestorJobMatchQuestionNode}
            urlParams={urlParams}
            allowURLParams={true}
            showExternalVariables={Boolean(graph.flow_config.api_bot_enabled)}
          />
        );
        break;

      case 'currency':
      case 'number':
      case 'integer':
      case 'double':
      case 'bigdecimal':
        const showOwnerVarToggle =
          attributeWritebackInfo?.attribute_name.includes('id') &&
          selectedRelatedEntity?.is_internal &&
          allowMeetingNodeVariables;
        const showOwnerVarSelect = showOwnerVarToggle && selectOwnerVar;

        valueInput = (
          <div className={css.column}>
            {showOwnerVarSelect ? (
              <MessageDraftEditor
                label=""
                placeholder={`${t(
                  'Select_a_node_variable',
                  'Select a node variable',
                )}`}
                defaultValue={
                  node.text_draft_json || createRawDraftContentState()
                }
                onChange={(text_draft_json) =>
                  setNode({...node, text_draft_json})
                }
                entityType={graph.anchor_entity_type}
                isFirstSmsMessage={false}
                allowGifs={false}
                allowEmojis={false}
                allowAtsVariables={false}
                error={error?.text_draft_json}
                readOnly={readOnly}
                useCase={graph.use_case}
                nodeVariables={nodeVariables.filter(
                  ({attribute_name}) => attribute_name === 'external_source_id',
                )}
                isOneLine
                urlParams={urlParams}
                allowURLParams={true}
                showExternalVariables={Boolean(
                  graph.flow_config.api_bot_enabled,
                )}
              />
            ) : (
              <Input
                placeholder={`${t(
                  'Enter_a_value_for_the_ATS_field',
                  'Enter a value for the ATS field',
                )}...`}
                value={Number(node.disposition_writeback_value)}
                onChange={(e) => {
                  setNode({
                    ...node,
                    disposition_writeback_value: e.target.value,
                  });
                }}
                disabled={readOnly}
                type="number"
              />
            )}
            {showOwnerVarToggle && (
              <Toggle
                labelPosition="right"
                name="toggle"
                onChange={() => {
                  setSelectOwnerVar(!selectOwnerVar);
                  if (node.disposition_writeback_value) {
                    //if this exists, and we are switching to the other input, we will be using text_draft_json, meaning these fields below need to be cleared out to avoid bad data
                    setNode({
                      ...node,
                      disposition_writeback_value: null,
                    });
                  } else {
                    setNode({
                      ...node,
                      text_draft_json: null,
                    });
                  }
                }}
                checked={selectOwnerVar}
                classNames={{wrapper: css.withMargin}}
              >
                {t('Select_Meeting_Owner', 'Select Meeting Owner')}
              </Toggle>
            )}
          </div>
        );
        break;

      case 'phone':
        valueInput = (
          <PhoneInput
            placeholder={`${t(
              'Enter_a_value_for_the_ATS_field',
              'Enter a value for the ATS field',
            )}...`}
            value={String(node.disposition_writeback_value || '')}
            onChange={(disposition_writeback_value) => {
              setNode({...node, disposition_writeback_value});
            }}
            disabled={readOnly}
          />
        );
        break;

      default:
        console.error('Invalid writeback data type.');
    }
  }

  return (
    <div className={css.questionNodeContainer}>
      <div className={css.section}>
        <Input
          label={t('Node_Title', 'Node Title')}
          value={node.name}
          onChange={(event) =>
            setNode({...node, name: event.currentTarget.value})
          }
        />
        {error?.name && (
          <div className={css.errorText}>
            {error.name
              ? error.name.errors?.includes('EMPTY')
                ? `${t(
                    'This_node_must_have_a_name',
                    'This node must have a name',
                  )}.`
                : `${t(
                    'This_name_is_already_taken',
                    'This name is already taken',
                  )}.`
              : `${t(
                  'Node_names_must_be_unique_40_characters_maximum',
                  'Node names must be unique. 40 characters maximum',
                )}.`}
          </div>
        )}
      </div>
      <div className={css.section}>
        <div className={css.inputLabel}>
          {t('ATS_Writeback_Field', 'ATS Writeback Field')}
        </div>
        <LoadedVariablePicker
          entityType={graph.anchor_entity_type}
          value={node.writeback_field_id}
          attributeMetadata={writebackAttributes}
          onChange={(attributeMetadata) => {
            const writebackFieldId = getWbFieldId(attributeMetadata);
            editorDispatch({
              type: 'set_writeback',
              payload: writebackFieldId,
              metadata: {
                previous: attributeWritebackInfo,
                next: attributeMetadata,
              },
            });
          }}
          readOnly={readOnly}
        />
        {error?.writeback_field_id && (
          <div className={css.errorText}>{`${t(
            'ATS_writeback_field_cannot_be_empty',
            'ATS writeback field cannot be empty',
          )}.`}</div>
        )}
      </div>
      <div className={css.section}>
        <div className={css.inputLabel}>{t('Value', 'Value')}</div>
        {valueInput}
      </div>

      <div className={css.footer}>
        <Button size="small" type="secondary" onClick={onCancel}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSaveNode}
          disabled={hasSubmitted && !!validation}
        >
          {t('Save', 'Save')}
        </Button>
      </div>
    </div>
  );
}
